import React from 'react';

import isEmpty from 'lodash/isEmpty';

import { margin } from '@lumapps/classnames';
import { FormSubSection, FormSubSectionProps } from '@lumapps/lumx-forms/components/FormSubSection';
import { GapSize, GlobalSize, Size, SkeletonRectangle, SkeletonTypography } from '@lumapps/lumx/react';

import { UseMetadataListWithSelected } from '../../hooks/useMetadataListWithSelected';
import { FormMetadataField, type FormMetadataFieldProps } from '../FormMetadataField';

export interface FormMetadataFieldsetProps extends FormSubSectionProps {
    /** metadatas to display */
    metadatas?: UseMetadataListWithSelected;
    /** gap to be used between fields */
    gap: GapSize;
    /** whether the each field should show multiple levels or just the first one */
    showMultipleLevels?: boolean;
    /** props passed to each MetadataField */
    metadataFieldProps?: Partial<FormMetadataFieldProps>;
    /** Restrict the selectable values to these exact metadata ids. Undefined means no restriction. */
    allowedMetadataIds?: string[];
    /** Hide a family that offers no value and holds none. Off by default, so callers opt in. */
    hideEmptyFamilies?: boolean;
}

export const FormMetadataFieldset: React.FC<FormMetadataFieldsetProps> = ({
    metadatas,
    gap,
    showMultipleLevels,
    metadataFieldProps,
    allowedMetadataIds,
    hideEmptyFamilies,
    ...props
}) => {
    const visibleParentMetadatas = React.useMemo(() => {
        const parents = metadatas?.parentMetadatas ?? [];
        // Callers that do not opt in keep the exact array they passed in.
        if (!hideEmptyFamilies || !metadatas) {
            return parents;
        }
        // No allow list means every value counts, so only a truly empty family is hidden.
        const allowed = allowedMetadataIds && new Set(allowedMetadataIds);
        return parents.filter(
            (family) =>
                // A family the content already uses stays visible. Hiding it makes the value
                // invisible and unremovable. The save payload also drops it.
                !isEmpty(metadatas.selectedMetadatas[family.id]) ||
                // `metadatas` and the picker's per-family list must agree on which values exist.
                // A value in one list but not the other becomes unreachable. They agree today
                // because `canBeAppliedTo` and `customContentTypes` are category-level fields.
                // Neither is ever set on a value.
                metadatas.metadatas.some(
                    (item) =>
                        item.familyKey === family.id && item.id !== family.id && (!allowed || allowed.has(item.id)),
                ),
        );
    }, [allowedMetadataIds, hideEmptyFamilies, metadatas]);

    if (metadatas && metadatas.isLoading) {
        return (
            <>
                <SkeletonTypography typography="body1" width="300px" className={margin('bottom')} />
                <SkeletonRectangle width={'100%' as GlobalSize} height={Size.m} className={margin('bottom', 'big')} />
                <SkeletonTypography typography="body1" width="300px" className={margin('bottom')} />
                <SkeletonRectangle width={'100%' as GlobalSize} height={Size.m} />
            </>
        );
    }

    if (metadatas && metadatas.hasLoaded && !metadatas.hasError && visibleParentMetadatas.length > 0) {
        return (
            <FormSubSection {...props} contentWrapperProps={{ gap, ...props.contentWrapperProps }}>
                {visibleParentMetadatas.map((parentMetadata) => (
                    <FormMetadataField
                        name={`metadatas.${parentMetadata.id}`}
                        key={parentMetadata.id}
                        family={parentMetadata}
                        controllerProps={{ defaultValue: metadatas.selectedMetadatas[parentMetadata.id] }}
                        showMultipleLevels={showMultipleLevels}
                        allowedMetadataIds={allowedMetadataIds}
                        {...metadataFieldProps}
                    />
                ))}
            </FormSubSection>
        );
    }

    return null;
};
